import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useRouteMatch } from 'react-router';
import { CustomPageType } from '../../../../../extViewmodels/sdk/models/CustomPageStyle';
import { useClientGetSurveyLogoByToken } from '../../../../../queries';
import { LogoIcon } from '../../../../../web/components/svgs/icons/LogoIcon';
import { black } from '../../../../../web/styles/colors';
import { htmlHasCustomButtonColors } from '../../../../../web/styles/customPageTheme';
import {
	ICustomSurvey,
	IDonationSurvey,
	IEventRegistrationSurvey,
	IFileRequestSurvey,
	ISatisfactionSurvey,
} from '../../../../models/Api';
import { useCustomPageStyleByToken } from '../../../../queries/useCustomPageStyleByToken';
import { applySurveyAccentVars } from '../../../../styles/customBrandedAccents';
import { SurveyHeader } from '../components/SurveyHeader';
import { styleSheet } from './styles';

interface IProps<
	T extends ISatisfactionSurvey | ICustomSurvey | IEventRegistrationSurvey | IDonationSurvey | IFileRequestSurvey,
> {
	cardStyles?: StyleDeclarationValue[];
	className?: string;
	contentStyles?: StyleDeclarationValue[];
	styles?: StyleDeclarationValue[];
	survey?: T;
	unavailableTitle?: string;
}

export function Survey<
	T extends ISatisfactionSurvey | ICustomSurvey | IEventRegistrationSurvey | IDonationSurvey | IFileRequestSurvey,
>({
	cardStyles = [],
	children,
	className = '',
	contentStyles = [],
	styles = [],
	survey,
	unavailableTitle = 'Survey',
}: React.PropsWithChildren<IProps<T>>) {
	const { params: urlParams = {} } = useRouteMatch<{ token?: string }>();

	const customHeaderQuery = useCustomPageStyleByToken({
		token: urlParams.token,
		type: CustomPageType.Header,
		enabled: !!urlParams.token,
	});
	const customHeaderData = customHeaderQuery.data;

	const { data: customFooterData } = useCustomPageStyleByToken({
		token: urlParams.token,
		type: CustomPageType.Footer,
		enabled: !!urlParams.token,
	});

	const customSurvey = survey?._type === 'CustomSurvey' ? (survey as ICustomSurvey) : undefined;

	const surveyLogo = useClientGetSurveyLogoByToken({
		accessToken: urlParams.token,
		enabled: survey?._type === 'CustomSurvey',
		refetchOnWindowFocus: false,
	});

	const renderCustomContent = React.useCallback((html: string, isHeader = false) => {
		const classNameStyle = isHeader ? css(styleSheet.customHeader) : css(styleSheet.customFooter);
		return (
			<div
				className={classNameStyle}
				dangerouslySetInnerHTML={{ __html: html }}
				{...(isHeader ? { 'data-survey-header': true } : {})}
			/>
		);
	}, []);

	const hasCustomHeader = !!customHeaderData?.html;
	const isHeaderLoading = customHeaderQuery.isLoading;

	const hasCustomButtonColors = React.useMemo(
		() => htmlHasCustomButtonColors(customHeaderData?.html),
		[customHeaderData?.html]
	);

	React.useEffect(() => {
		if (!hasCustomButtonColors) {
			return;
		}
		return applySurveyAccentVars();
	}, [hasCustomButtonColors]);

	const shouldShowSurveyNameWithCustomHeader = React.useMemo(() => {
		if (!hasCustomHeader || !survey?.name) {
			return false;
		}

		switch (survey._type) {
			case 'CustomSurvey':
				return survey.options?.showSurveyName !== false;
			case 'EventRegistrationSurvey':
				return survey.showSurveyName !== false;
			default:
				// Only Custom and Event Registration surveys expose a "show survey name" toggle. Satisfaction
				// and Donation surveys have no such option, so they intentionally never render the name above
				// the body when a custom header replaces the default header.
				return false;
		}
	}, [hasCustomHeader, survey]);

	const onRenderCustomHeader = React.useCallback(() => {
		if (customHeaderData?.html) {
			return renderCustomContent(customHeaderData.html, true);
		}
		return null;
	}, [customHeaderData?.html, renderCustomContent]);

	const onRenderCustomFooter = React.useCallback(() => {
		if (customFooterData?.html) {
			return renderCustomContent(customFooterData.html, false);
		}
		return null;
	}, [customFooterData?.html, renderCustomContent]);

	return (
		<div className={`survey-container ${className} ${css(styleSheet.container, ...styles)}`}>
			<div className={css(styleSheet.content, ...cardStyles)}>
				{onRenderCustomHeader() ||
					(!isHeaderLoading ? (
						<SurveyHeader
							companyDisplayName={customSurvey ? customSurvey.options?.companyName : survey?.companyDisplayName}
							name={survey?.name || unavailableTitle}
							logoUrl={surveyLogo.data}
							styles={
								survey?._type === 'EventRegistrationSurvey'
									? [styleSheet.headerEvent]
									: survey?._type === 'SatisfactionSurvey'
										? [styleSheet.headerSatisfaction]
										: []
							}
						/>
					) : null)}

				<div className={css(styleSheet.body, ...contentStyles)}>
					{shouldShowSurveyNameWithCustomHeader ? <h2 className={css(styleSheet.surveyName)}>{survey?.name}</h2> : null}
					{children}
				</div>
			</div>
			<div className={css(styleSheet.footer)}>
				{onRenderCustomFooter() || (
					<>
						<div className={css(styleSheet.poweredBy)}>Powered by</div>
						<a href='https://levitate.ai' target='_blank' rel='noopener noreferrer'>
							<LogoIcon className={css(styleSheet.mainLogo)} textColor={black} />
						</a>
					</>
				)}
			</div>
		</div>
	);
}
